/**
 * Lógica de cálculo — funciones puras, sin React.
 *
 * Todo el cálculo vive aquí y no dentro de los componentes. Así se
 * puede probar con `node --test` y una fórmula fiscal se corrige en un
 * solo sitio.
 */

import {
  TARIFA_ISR_MENSUAL,
  SUBSIDIO_EMPLEO,
  CUOTAS_IMSS_OBRERO,
  UMA,
  TASAS_INFONAVIT,
  APORTACION_PATRONAL_INFONAVIT_PCT,
  diasVacacionesPorAntiguedad,
} from "../data/fiscal.js";

const num = (v, def = 0) => {
  const n = typeof v === "number" ? v : parseFloat(v);
  return Number.isFinite(n) ? n : def;
};

export const redondear = (n, d = 2) => Math.round((num(n) + Number.EPSILON) * 10 ** d) / 10 ** d;

export const formatoMXN = (n, maximumFractionDigits = 2) =>
  new Intl.NumberFormat("es-MX", {
    style: "currency",
    currency: "MXN",
    maximumFractionDigits,
  }).format(num(n));

/* ══════════════════════════════════ IVA ═════════════════════════════ */

/**
 * @param {number} monto  Subtotal (modo "agregar") o total (modo "desglosar").
 * @param {number} tasa   Tasa de IVA en porcentaje.
 * @param {"agregar"|"desglosar"} modo
 */
export function calcularIva(monto, tasa, modo = "agregar") {
  const m = num(monto);
  const t = num(tasa);
  if (modo === "agregar") {
    const iva = m * (t / 100);
    return { subtotal: m, iva, total: m + iva };
  }
  const subtotal = m / (1 + t / 100);
  return { subtotal, iva: m - subtotal, total: m };
}

/* ═══════════════════════════════ Porcentajes ════════════════════════ */

export const porcentajeDe = (pct, total) => (num(total) * num(pct)) / 100;

export const queePorcentajeEs = (parte, total) => {
  const t = num(total);
  return t === 0 ? 0 : (num(parte) / t) * 100;
};

export const variacionPorcentual = (inicial, final) => {
  const i = num(inicial);
  return i === 0 ? 0 : ((num(final) - i) / Math.abs(i)) * 100;
};

/* ═══════════════════════════ Sueldo neto (ISR/IMSS) ═════════════════ */

/** Retención de ISR sobre un ingreso mensual gravable. */
export function calcularIsrMensual(ingresoMensual) {
  const ingreso = num(ingresoMensual);
  if (ingreso <= 0) return { isr: 0, tramo: null, subsidio: 0, isrACargo: 0 };

  let tramo = TARIFA_ISR_MENSUAL[0];
  for (const t of TARIFA_ISR_MENSUAL) {
    if (ingreso >= t.limiteInferior) tramo = t;
    else break;
  }

  const excedente = ingreso - tramo.limiteInferior;
  const isr = tramo.cuotaFija + excedente * (tramo.tasa / 100);

  // Subsidio para el empleo: sólo hasta el tope de ingreso.
  const subsidio = ingreso <= SUBSIDIO_EMPLEO.topeIngresoMensual ? SUBSIDIO_EMPLEO.montoMensual : 0;

  return { isr, tramo, subsidio, isrACargo: Math.max(0, isr - subsidio) };
}

/**
 * Salario Base de Cotización.
 * Factor de integración = 1 + aguinaldo/365 + (vacaciones × prima%)/365
 */
export function calcularSbc({ sueldoMensual, diasVacaciones, primaVacacionalPct, diasAguinaldo }) {
  const salarioDiario = num(sueldoMensual) / 30;
  const factorIntegracion =
    1 + num(diasAguinaldo) / 365 + (num(diasVacaciones) * (num(primaVacacionalPct) / 100)) / 365;
  return { salarioDiario, factorIntegracion, sbc: salarioDiario * factorIntegracion };
}

/** Cuotas obrero del IMSS sobre el SBC diario, mensualizadas (×30). */
export function calcularCuotasImss(sbcDiario) {
  const sbc = Math.min(num(sbcDiario), UMA.diaria * 25);
  if (sbc <= 0) return { total: 0, desglose: [] };

  const topeExento = UMA.diaria * CUOTAS_IMSS_OBRERO.excedente.umasExentas;
  const c = CUOTAS_IMSS_OBRERO;

  const desglose = [
    {
      concepto: c.excedente.concepto,
      tasa: c.excedente.tasa,
      importe: sbc > topeExento ? (sbc - topeExento) * (c.excedente.tasa / 100) * 30 : 0,
    },
    { concepto: c.prestacionesDinero.concepto, tasa: c.prestacionesDinero.tasa, importe: sbc * (c.prestacionesDinero.tasa / 100) * 30 },
    { concepto: c.gastosMedicosPensionados.concepto, tasa: c.gastosMedicosPensionados.tasa, importe: sbc * (c.gastosMedicosPensionados.tasa / 100) * 30 },
    { concepto: c.invalidezVida.concepto, tasa: c.invalidezVida.tasa, importe: sbc * (c.invalidezVida.tasa / 100) * 30 },
    { concepto: c.cesantiaVejez.concepto, tasa: c.cesantiaVejez.tasa, importe: sbc * (c.cesantiaVejez.tasa / 100) * 30 },
  ];

  return { total: desglose.reduce((a, d) => a + d.importe, 0), desglose };
}

/** Cálculo completo de sueldo neto. */
export function calcularSueldoNeto({
  sueldoMensual,
  aniosAntiguedad = 1,
  diasVacaciones,
  primaVacacionalPct = 25,
  diasAguinaldo = 15,
}) {
  const bruto = num(sueldoMensual);
  const vacaciones = diasVacaciones ?? diasVacacionesPorAntiguedad(aniosAntiguedad);

  const { salarioDiario, factorIntegracion, sbc } = calcularSbc({
    sueldoMensual: bruto,
    diasVacaciones: vacaciones,
    primaVacacionalPct,
    diasAguinaldo,
  });

  const { isr, tramo, subsidio, isrACargo } = calcularIsrMensual(bruto);
  const imss = calcularCuotasImss(sbc);
  const neto = Math.max(0, bruto - isrACargo - imss.total);

  return {
    bruto,
    diasVacaciones: vacaciones,
    salarioDiario,
    factorIntegracion,
    sbc,
    isrBruto: isr,
    subsidio,
    isr: isrACargo,
    tramoIsr: tramo,
    imss: imss.total,
    imssDesglose: imss.desglose,
    neto,
    tasaEfectiva: bruto > 0 ? ((isrACargo + imss.total) / bruto) * 100 : 0,
  };
}

/* ══════════════════════════════ Interés ═════════════════════════════ */

export function calcularInteres({
  capital,
  tasaAnual,
  plazo,
  unidadPlazo = "years",
  tipo = "compound",
  capitalizacionesPorAnio = 12,
}) {
  const C = num(capital);
  const i = num(tasaAnual) / 100;
  const p = num(plazo);
  const anios = unidadPlazo === "years" ? p : p / 12;
  const periodos = Math.max(0, Math.round(p));

  const tabla = [];

  if (tipo === "simple") {
    const interes = C * i * anios;
    const porPeriodo = periodos > 0 ? interes / periodos : 0;
    let saldo = C;
    let acumulado = 0;
    for (let n = 1; n <= Math.min(periodos, 600); n++) {
      acumulado += porPeriodo;
      tabla.push({ periodo: n, saldoInicial: saldo, interes: porPeriodo, saldoFinal: saldo + porPeriodo, acumulado });
      saldo += porPeriodo;
    }
    return { interes, total: C + interes, tabla };
  }

  const m = Math.max(1, num(capitalizacionesPorAnio, 12));
  const total = C * Math.pow(1 + i / m, m * anios);
  const interes = total - C;

  // Tasa efectiva del periodo que muestra la tabla (año o mes).
  const tasaPeriodo = unidadPlazo === "years" ? Math.pow(1 + i / m, m) - 1 : i / 12;
  let saldo = C;
  let acumulado = 0;
  for (let n = 1; n <= Math.min(Math.max(periodos, 1), 600); n++) {
    const gen = saldo * tasaPeriodo;
    acumulado += gen;
    tabla.push({ periodo: n, saldoInicial: saldo, interes: gen, saldoFinal: saldo + gen, acumulado });
    saldo += gen;
  }

  return { interes, total, tabla };
}

/* ════════════════════════════ INFONAVIT ═════════════════════════════ */

/** Tasa y tope de descuento sugeridos según el ingreso mensual. */
export function propuestaInfonavit(ingresoMensual) {
  const ingreso = num(ingresoMensual);
  const tramo = TASAS_INFONAVIT.find((t) => ingreso <= t.ingresoHasta) ?? TASAS_INFONAVIT.at(-1);
  return {
    tasaAnual: tramo.tasaAnual,
    descuentoMaximoPct: tramo.descuentoMaximoPct,
    descuentoPropuesto: ingreso * (tramo.descuentoMaximoPct / 100),
    umas: UMA.mensual > 0 ? ingreso / UMA.mensual : 0,
  };
}

/** Pago mensual de una anualidad (sistema francés). */
export function pagoMensual(monto, tasaAnual, plazoAnios) {
  const P = num(monto);
  const r = num(tasaAnual) / 12 / 100;
  const n = Math.round(num(plazoAnios) * 12);
  if (P <= 0 || n <= 0) return 0;
  if (r === 0) return P / n;
  return (P * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

/**
 * Tabla de amortización con aportación patronal y pagos extra.
 *
 * @returns {{tabla:Array, meses:number, interesTotal:number,
 *            aportacionPatronalTotal:number, extraTotal:number,
 *            liquidado:boolean}}
 */
export function amortizarInfonavit({
  monto,
  tasaAnual,
  plazoAnios,
  sueldoMensual = 0,
  pagoPersonalizado = null,
  aplicarAportacionPatronal = true,
  pagosExtra = null,
}) {
  const P = num(monto);
  const r = num(tasaAnual) / 12 / 100;
  const nTotal = Math.round(num(plazoAnios) * 12);
  if (P <= 0 || nTotal <= 0) {
    return { tabla: [], meses: 0, interesTotal: 0, aportacionPatronalTotal: 0, extraTotal: 0, liquidado: false };
  }

  const pagoBase = pagoMensual(P, tasaAnual, plazoAnios);
  const pago = pagoPersonalizado !== null && pagoPersonalizado > 0 ? num(pagoPersonalizado) : pagoBase;
  const aportacion = aplicarAportacionPatronal
    ? num(sueldoMensual) * (APORTACION_PATRONAL_INFONAVIT_PCT / 100)
    : 0;

  let saldo = P;
  let interesTotal = 0;
  let aportacionTotal = 0;
  let extraTotal = 0;
  let mes = 0;
  const tabla = [];

  // Tope duro: 1.5× el plazo. Si el pago no cubre ni el interés, el
  // saldo crece y el préstamo nunca se liquida — hay que cortar.
  const limite = Math.ceil(nTotal * 1.5);

  while (saldo > 0.01 && mes < limite) {
    mes++;
    const saldoInicial = saldo;
    const interes = saldo * r;

    let capital = 0;
    if (pago < interes) {
      saldo += interes - pago; // amortización negativa
    } else {
      capital = Math.min(pago - interes, saldo);
      saldo -= capital;
    }

    const aport = Math.min(aportacion, saldo);
    saldo -= aport;
    aportacionTotal += aport;

    let extra = 0;
    if (pagosExtra && saldo > 0) {
      extra = Math.min(num(montoExtraDelMes(pagosExtra, mes)), saldo);
      saldo -= extra;
      extraTotal += extra;
    }

    interesTotal += interes;

    if (tabla.length < 360) {
      tabla.push({
        mes,
        saldoInicial,
        pago: Math.min(pago, saldoInicial + interes),
        interes,
        capital,
        aportacionPatronal: aport,
        extra,
        saldoFinal: saldo,
      });
    }
  }

  return {
    tabla,
    meses: mes,
    interesTotal,
    aportacionPatronalTotal: aportacionTotal,
    extraTotal,
    pagoBase,
    liquidado: saldo <= 0.01,
  };
}

/**
 * @param {{modo:"regular"|"unico", monto:number, frecuencia?:"mensual"|"anual",
 *          anioInicio?:number, anioFin?:number, mesUnico?:number}} cfg
 */
function montoExtraDelMes(cfg, mes) {
  if (!cfg || !cfg.monto) return 0;
  if (cfg.modo === "unico") return mes === (cfg.mesUnico ?? 1) ? cfg.monto : 0;

  const anio = Math.ceil(mes / 12);
  const desde = cfg.anioInicio ?? 1;
  const hasta = cfg.anioFin ?? Infinity;
  if (anio < desde || anio > hasta) return 0;
  return cfg.frecuencia === "mensual" || mes % 12 === 0 ? cfg.monto : 0;
}

/* ═══════════════════════════ Negocio / ROI ══════════════════════════ */

export function calcularMargen(costo, precioVenta) {
  const c = num(costo);
  const p = num(precioVenta);
  const utilidad = p - c;
  return {
    utilidad,
    margenPct: p === 0 ? 0 : (utilidad / p) * 100, // sobre el precio
    markupPct: c === 0 ? 0 : (utilidad / c) * 100, // sobre el costo
  };
}

export function calcularRoi(inversion, retorno) {
  const i = num(inversion);
  return { ganancia: num(retorno) - i, roiPct: i === 0 ? 0 : ((num(retorno) - i) / i) * 100 };
}

export function puntoEquilibrio(costosFijos, precioUnitario, costoVariableUnitario) {
  const margenContribucion = num(precioUnitario) - num(costoVariableUnitario);
  if (margenContribucion <= 0) return { unidades: Infinity, ingresos: Infinity, margenContribucion };
  const unidades = num(costosFijos) / margenContribucion;
  return { unidades, ingresos: unidades * num(precioUnitario), margenContribucion };
}

/* ═══════════════════════════════ Freelance ══════════════════════════ */

export function calcularTarifaFreelance({
  netoDeseadoMensual,
  gastosOperativosMensuales = 0,
  impuestoPct = 0,
  semanasLibresAlAnio = 4,
  horasPorSemana = 40,
  aprovechamientoPct = 70,
}) {
  const neto = num(netoDeseadoMensual);
  const gastos = num(gastosOperativosMensuales);
  const imp = Math.min(num(impuestoPct), 99) / 100;

  const brutoMensual = (neto + gastos) / (1 - imp || 1);
  const brutoAnual = brutoMensual * 12;

  const semanasTrabajadas = Math.max(1, 52 - num(semanasLibresAlAnio));
  const horasAnuales = semanasTrabajadas * num(horasPorSemana);
  const horasFacturables = horasAnuales * (num(aprovechamientoPct, 70) / 100);

  const tarifaHora = horasFacturables > 0 ? brutoAnual / horasFacturables : 0;

  return {
    brutoMensual,
    brutoAnual,
    horasFacturables,
    tarifaHora,
    tarifaDia: tarifaHora * 8,
    tarifaSemana: tarifaHora * num(horasPorSemana),
  };
}

/* ═══════════════════════════ Presupuesto ════════════════════════════ */

export function presupuesto503020(ingresoTotal, { necesidades = 0, deseos = 0, ahorro = 0 } = {}) {
  const ingreso = num(ingresoTotal);
  const sugerido = { necesidades: ingreso * 0.5, deseos: ingreso * 0.3, ahorro: ingreso * 0.2 };
  const real = { necesidades: num(necesidades), deseos: num(deseos), ahorro: num(ahorro) };
  const gastado = real.necesidades + real.deseos;

  return {
    ingreso,
    sugerido,
    real,
    sobrante: ingreso - gastado - real.ahorro,
    porcentajes: {
      necesidades: ingreso ? (real.necesidades / ingreso) * 100 : 0,
      deseos: ingreso ? (real.deseos / ingreso) * 100 : 0,
      ahorro: ingreso ? (real.ahorro / ingreso) * 100 : 0,
    },
  };
}

/* ════════════════════════════ Descuentos ════════════════════════════ */

/** Descuentos encadenados: 20% + 10% NO es 30%. */
export function aplicarDescuentos(precioOriginal, descuentosPct = []) {
  const original = num(precioOriginal);
  let precio = original;
  const pasos = [];
  for (const d of descuentosPct) {
    const pct = Math.min(num(d), 100);
    if (pct <= 0) continue;
    const rebaja = precio * (pct / 100);
    precio -= rebaja;
    pasos.push({ pct, rebaja, precio });
  }
  return {
    precioOriginal: original,
    precioFinal: precio,
    ahorro: original - precio,
    descuentoEfectivoPct: original ? ((original - precio) / original) * 100 : 0,
    pasos,
  };
}

export const precioUnitario = (precio, cantidad) => {
  const c = num(cantidad);
  return c === 0 ? 0 : num(precio) / c;
};

/* ═══════════════════════════════ IMC ════════════════════════════════ */

export function calcularImc(pesoKg, alturaCm) {
  const peso = num(pesoKg);
  const m = num(alturaCm) / 100;
  if (peso <= 0 || m <= 0) return { imc: 0, categoria: "Ingresa valores", pesoIdeal: null };

  const imc = peso / (m * m);
  let categoria = "Obesidad";
  if (imc < 18.5) categoria = "Bajo peso";
  else if (imc < 25) categoria = "Peso normal";
  else if (imc < 30) categoria = "Sobrepeso";

  return {
    imc,
    categoria,
    pesoIdeal: { min: 18.5 * m * m, max: 24.9 * m * m },
  };
}

/* ══════════════════════════════ Viaje ═══════════════════════════════ */

export function calcularViaje({
  distanciaKm,
  rendimientoKmPorLitro,
  precioLitro,
  casetas = 0,
  pasajeros = 1,
  idaYVuelta = false,
}) {
  const distancia = num(distanciaKm) * (idaYVuelta ? 2 : 1);
  const rendimiento = num(rendimientoKmPorLitro);
  const litros = rendimiento > 0 ? distancia / rendimiento : 0;
  const combustible = litros * num(precioLitro);
  const peajes = num(casetas) * (idaYVuelta ? 2 : 1);
  const total = combustible + peajes;
  const personas = Math.max(1, Math.round(num(pasajeros, 1)));

  return { distancia, litros, combustible, peajes, total, porPasajero: total / personas };
}

/* ════════════════════════════ Medidas ═══════════════════════════════ */

export function convertirMedida(valor, desde, hacia, categoria) {
  const v = num(valor);
  if (categoria.baseUnit === "°C") {
    const aCelsius = { "°C": (x) => x, "°F": (x) => ((x - 32) * 5) / 9, K: (x) => x - 273.15 };
    const deCelsius = { "°C": (x) => x, "°F": (x) => (x * 9) / 5 + 32, K: (x) => x + 273.15 };
    return deCelsius[hacia](aCelsius[desde](v));
  }
  const f = (u) => categoria.units.find((x) => x.value === u)?.factor ?? 1;
  return (v * f(desde)) / f(hacia);
}

/**
 * Presupuesto de obra.
 * Cada partida: { id, ratio?, cantidad?, precio, activa }. Si trae `ratio`, la
 * cantidad es metros × ratio; si no, se usa `cantidad` fija. Sobre el costo
 * directo se aplican, en cascada: desperdicio, indirectos, utilidad e IVA.
 */
export function presupuestarObra({
  metros,
  partidas = [],
  desperdicioPct = 0,
  indirectosPct = 0,
  utilidadPct = 0,
  ivaPct = 16,
}) {
  const m = Math.max(num(metros), 0);
  const lineas = partidas.map((p) => {
    const cantidad = p.ratio != null ? m * Math.max(num(p.ratio), 0) : Math.max(num(p.cantidad), 0);
    const importe = p.activa === false ? 0 : cantidad * Math.max(num(p.precio), 0);
    return { id: p.id, cantidad, importe };
  });
  const directo = lineas.reduce((a, l) => a + l.importe, 0);
  const desperdicio = directo * (Math.max(num(desperdicioPct), 0) / 100);
  const baseIndirectos = directo + desperdicio;
  const indirectos = baseIndirectos * (Math.max(num(indirectosPct), 0) / 100);
  const utilidad = (baseIndirectos + indirectos) * (Math.max(num(utilidadPct), 0) / 100);
  const subtotal = baseIndirectos + indirectos + utilidad;
  const iva = subtotal * (Math.max(num(ivaPct), 0) / 100);
  const total = subtotal + iva;
  return { lineas, directo, desperdicio, indirectos, utilidad, subtotal, iva, total, porM2: m > 0 ? total / m : 0 };
}
